/* eslint-disable @typescript-eslint/explicit-function-return-type */
import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import 'lib/bootstrap-grid-namespaced.scss'
import StoreFunctions from 'stores/storeFunctions'
import { GemstoneSearchPage } from '@unbridaled/search-sdk'
import { Preloader } from 'lib/components/preloader'
import { ErrorModal } from 'lib/components/modals/ErrorModal'
import { ConfirmationModal } from 'lib/components/modals/ConfirmationModal'
import { DASOrderSuccessfullModal } from 'lib/components/modals/DASOrderSuccessfullModal'
import { BookmarksModal } from 'lib/components/modals/BookmarksModal'
import { LoginModal } from 'lib/components/modals/LoginModal'
import { useWishlist } from 'utils/use-wishlist'
import { useSearchParamsProps } from 'utils/use-search-params-props'
import { truncateCurrencyAmount } from 'constants/currencies'
import { Roles, UserRoles } from 'constants/roles'
import { JewelryType } from 'constants/jewelryTypes'
import logger from 'utils/logger'
import { Config, DiamondsMainSearchPageState, Diamond, GemstoneSearchProps } from 'types/interfaces'
import { Menu, MenuButton, MenuItems, MenuLabel } from '@unbridaled/ui-components/Menu'
import { Button } from '@unbridaled/ui-components/Button'
import { ArrowHeadDownIcon } from '@unbridaled/icons/ArrowHeadDownIcon'
import { useMediaBreakpoint } from '@unbridaled/react-hooks'
import { LogoutIcon } from '@unbridaled/icons/LogoutIcon'
import classNames from 'classnames'
import { useAppContext } from 'lib/context/AppContext'
import SimpleStore from 'stores/simpleStore'
import { getSearchSdkLocale, serverErrorMessage } from 'i18n'
import { getLoginModalLabels } from 'lib/components/modals/loginModalLabels'

export const GemstonesMainSearchPage: React.FC<any> = props => {
  const { t } = useTranslation()
  const { diamondStore, appConfig, enabledDiamondGemTypes, typeLabels, currencyInfo } = useAppContext()
  const storeFunctions = new StoreFunctions()
  const simpleStore = useMemo(() => new SimpleStore(), [])
  const stoneTypeRedirect = true;
  const { getDefaultParams, onSelectedDiamondChange } = useSearchParamsProps({ stoneTypeRedirect })
  const USER_COOKIE_NAME = 'UNBRIDALED_USER'
  const [userData, setUserData] = useState(storeFunctions.getUser());
  const [role, setRole] = useState(storeFunctions.getUserRole())
  const [isMemoMode, setIsMemoMode] = useState(storeFunctions.isMemoMode())
  const [loaded, setLoaded] = useState(false)
  const [shopName, setShopName] = useState(null)
  const [shopCurrency, setShopCurrency] = useState<string | null>(null)
  const [config] = useState<Config>({
    sourceTypes: appConfig?.source_types,
    advancedSearchCartEnabled: appConfig?.advanced_search_cart_enabled,
    ringSizeRegion: appConfig?.ring_size_region
  })
  const [selectedGems, setSelectedGems] = useState<DiamondsMainSearchPageState['selectedGems']>([])
  const [selectedGemsOrderType, setSelectedGemsOrderType] = useState('i')

  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false)
  const [dasOrderSuccessfullModalOpen, setDasOrderSuccessfullModalOpen] = useState(false)
  const [bookmarksModalOpen, setBookmarksModalOpen] = useState(false)

  const [errorModalOpen, setErrorModalOpen] = useState(false)
  const [error, setError] = useState<DiamondsMainSearchPageState['error'] | null>(null)

  const [loginModalConfig, setLoginModalConfig] = useState({
    open: false,
    loadingOTP: false,
    mode: 'email',
    value: '',
    methodId: '',
    email: ''
  })

  const isMobile = !useMediaBreakpoint('sm')

  const wishlistProps = useWishlist('gemstone')

  const inFlightSearchRequestsRef = useRef<Map<string, Promise<any>>>(new Map())
  const searchPayloadCacheRef = useRef<Map<string, any>>(new Map())

  const prepareSearchParams = useCallback(
    (params: any) => {
      const searchParams = { ...params }
      // Always request DAS in the shop's merchant (base) currency for every
      // role/mode, so retail_price comes back in the merchant currency (e.g. AUD)
      // — not USD. Display then applies the Shopify presentment rate to the
      // customer currency. Sending 'USD' for merchant/memo returned USD prices
      // mislabeled as the shop currency.
      searchParams.currency_code = shopCurrency || currencyInfo.currency
      searchParams.include_total = true
      delete searchParams.total_only
      return searchParams
    },
    [shopCurrency, currencyInfo.currency]
  )

  const normalizeRetailPrice = useCallback((value: unknown): number => {
    const parsed = typeof value === 'string' ? Number(value.replace(/[\,]/g, '')) : Number(value)
    if (!Number.isFinite(parsed)) return 0
    return Math.round(parsed * 100) / 100
  }, [])

  const mapGemstonesForDisplay = useCallback(
    (gems: any[] = []): any[] => {
      const isRegularMode = role !== Roles.Merchant && !isMemoMode
      const shopifyRate =
        shopCurrency && currencyInfo.currency !== shopCurrency
          ? window.Shopify?.currency?.rate || 1
          : 1

      return gems.map(gem => {
        const storeRetailPrice = normalizeRetailPrice(gem?.retail_price)
        if (isRegularMode) {
          const converted = truncateCurrencyAmount(storeRetailPrice * shopifyRate)
          console.log(
            `[price][gemstones-main-search] ${shopCurrency || 'base'} ${storeRetailPrice} -> ${currencyInfo.currency} ${converted} @ Shopify rate ${shopifyRate}`
          )
          return {
            ...gem,
            retail_price: converted,
            currency_code: currencyInfo.currency
          }
        }

        // Merchant/memo (double) mode: keep retail_price in the shop currency for
        // the primary line; the customer-currency (second) line is retail_price x
        // the Shopify presentment rate, matching the other surfaces (not DAS's rate).
        const cartConverted = truncateCurrencyAmount(storeRetailPrice * shopifyRate)
        console.log(
          `[price][gemstones-main-search][double] ${shopCurrency || 'base'} ${storeRetailPrice} -> ${currencyInfo.currency} ${cartConverted} @ Shopify rate ${shopifyRate}`
        )
        return {
          ...gem,
          retail_price: storeRetailPrice,
          cart_price: cartConverted,
          currency_code: shopCurrency || gem?.currency_code || currencyInfo.currency
        }
      })
    },
    [role, isMemoMode, shopCurrency, currencyInfo.currency, normalizeRetailPrice]
  )

  const buildSearchRequestKey = useCallback((searchParams: any): string => {
    try {
      return JSON.stringify(searchParams)
    } catch (_e) {
      return JSON.stringify(searchParams)
    }
  }, [])

  const fetchGemstonesPayload = useCallback(
    async (params: any, signal?: AbortSignal) => {
      const searchParams = prepareSearchParams(params)
      const cacheKey = buildSearchRequestKey(searchParams)

      const cachedPayload = searchPayloadCacheRef.current.get(cacheKey)
      if (cachedPayload) {
        return { payload: cachedPayload, searchParams }
      }

      const existingRequest = inFlightSearchRequestsRef.current.get(cacheKey)
      if (existingRequest) {
        const payload = await existingRequest
        return { payload, searchParams }
      }

      const requestPromise = diamondStore
        .runFilter(searchParams, 'gemstone_search', signal)
        .then((payload: any) => {
          const mappedPayload = payload
            ? {
                ...payload,
                gems: mapGemstonesForDisplay(payload?.gems || [])
              }
            : payload
          if (searchPayloadCacheRef.current.size > 30) {
            searchPayloadCacheRef.current.clear()
          }
          searchPayloadCacheRef.current.set(cacheKey, mappedPayload)
          return mappedPayload
        })
        .finally(() => {
          if (inFlightSearchRequestsRef.current.get(cacheKey) === requestPromise) {
            inFlightSearchRequestsRef.current.delete(cacheKey)
          }
        })

      inFlightSearchRequestsRef.current.set(cacheKey, requestPromise)

      // Remove immediately on abort so subsequent calls with a fresh signal
      // don't await the aborting promise and silently discard a valid response.
      if (signal) {
        const removeEntry = () => {
          if (inFlightSearchRequestsRef.current.get(cacheKey) === requestPromise) {
            inFlightSearchRequestsRef.current.delete(cacheKey)
          }
        }
        if (signal.aborted) {
          removeEntry()
        } else {
          signal.addEventListener('abort', removeEntry, { once: true })
        }
      }
      const payload = await requestPromise
      return { payload, searchParams }
    },
    [diamondStore, prepareSearchParams, buildSearchRequestKey, mapGemstonesForDisplay]
  )

  const getDiamonds = useCallback(
    async (params: any, signal?: AbortSignal) => {
      try {
        const { payload: diamondsData, searchParams } = await fetchGemstonesPayload(params, signal)
        if (diamondsData && diamondsData.metadata) {
          return {
            offset: searchParams.offset || 0,
            total: diamondsData.metadata.total_count,
            data: diamondsData.gems.map((gem: Diamond) => ({
              cost: 0,
              wholesale_price: 0,
              ...gem,
            })),
            limit: diamondsData.metadata.limit
          }
        }
        return null
      } catch (e) {
        logger.info(e)
        return null
      }
    },
    [fetchGemstonesPayload]
  )

  const getDiamondsCount = useCallback(
    async (params: any, signal?: AbortSignal) => {
      try {
        const { payload } = await fetchGemstonesPayload(params, signal)
        return { total: payload?.metadata?.total_count || 0 }
      } catch (e) {
        logger.info(e)
        return null
      }
    },
    [fetchGemstonesPayload]
  )

  const fetchShopName = async (): Promise<void | null> => {
    try {
      const shopInfo = await diamondStore.getShopName()
      setShopName(shopInfo.shopName)
    } catch (e) {
      logger.error('Failed to get shop name')
      return null
    }
  }

  const fetchShopCurrency = async (): Promise<void | null> => {
    try {
      const currencyData = await simpleStore.ajax('GET', 'shop_currency')
      if (currencyData?.currency) {
        setShopCurrency(currencyData.currency)
        return
      }
      throw new Error('Failed to get shop currency')
    } catch (e) {
      logger.error('Failed to get shop currency')
      return null
    }
  }

  const onLogout = async (): Promise<void> => {
    setRole(UserRoles.MerchantRetail)
    await new Promise(resolve => setTimeout(resolve, 600))
    localStorage.removeItem(USER_COOKIE_NAME)
    setIsMemoMode(false)
    setRole(Roles.Customer)
    wishlistProps?.refresh(Roles.Customer, false)
  }

  const onSwitchToMemoMode = (): void => {
    const userData = JSON.parse(localStorage.getItem(USER_COOKIE_NAME) || '{}')
    localStorage.setItem(
      USER_COOKIE_NAME,
      JSON.stringify({
        ...userData,
        user: {
          ...userData.user,
          roles: [UserRoles.MerchantRetail],
          is_owner: false
        }
      })
    )
    setRole(Roles.Customer)
    setIsMemoMode(true)
    wishlistProps?.refresh(Roles.Customer, true)
  }

  useEffect(() => {
    const loadPageData = async (): Promise<void> => {
      await Promise.all([fetchShopName(), fetchShopCurrency()])
      setLoaded(true)
      const url = new URL(window.location.href);
      if (url.searchParams.get('admin') === 'true') {
        onLogout()
        setLoginModalConfig(prevConfig => ({
          ...prevConfig,
          open: true
        }));
      }
    }

    loadPageData()

    function handleMessage(event: { data: string }): void {
      const certEl = document.querySelector('[class^=Certificate_module_close]')
      const mediaEl = document.querySelector('[class^=MediaPlayer_module_close]')
      if (event.data === 'ubCertificateViewerClosed' && certEl !== null) {
        (certEl as HTMLElement).click()
      } else if (event.data === 'ubMediaViewerClosed' && mediaEl !== null) {
        (mediaEl as HTMLElement).click()
      }
    }
    window.addEventListener('message', handleMessage, false)

    const closeViewer = (e: { key: string }): void => {
      if (e.key === 'Escape') {
        let moduleClass: string | null = null
        moduleClass = '_module_close'
        const element = document.querySelector('[class*=' + moduleClass + ']')
        if (element !== null) {
          (element as HTMLElement).click()
        }
      }
    }
    window.addEventListener('keyup', closeViewer)
    return () => {
      window.removeEventListener('message', handleMessage)
      window.removeEventListener('keyup', closeViewer)
    }
  }, [])

  const onParamsChange = useCallback(
    (filters: { [key: string]: any }): void => {
      const searchParams = new URLSearchParams(window.location.search)
      let stoneType: string | null = null;
      if (Object.keys(filters).length === 0) {
        searchParams.delete('filters')
      } else {
        const queryString = btoa(JSON.stringify(filters))
        const params = JSON.parse(atob(queryString))

        for (const key in params) {
          const value = params[key].toString()
          if (key === 'stone_type') {
            stoneType = value
            delete params[key]
          }
        }
        const updatedFilters = btoa(JSON.stringify(params))
        searchParams.set('filters', updatedFilters)
      }

      const pathname = window.location.pathname;
      const base = stoneType
        ? pathname.slice(0, pathname.lastIndexOf('/')) + `/${stoneType}`
        : pathname;

      const newUrl = `${base}?${searchParams.toString()}`;

      window.history.replaceState(null, '', newUrl);

    },
    []
  )

  const closeErrorModal = (): void => {
    setError(null)
    setErrorModalOpen(false)
  }

  const closeBookmarksModal = (): void => {
    setBookmarksModalOpen(false)
  }

  const closeConfirmationModal = (): void => {
    setConfirmationModalOpen(false)
  }

  const closeDASOrderSuccessfullModal = (): void => {
    setDasOrderSuccessfullModalOpen(false)
  }

  const handleLoginModalValueChange = (value: string, mode: string): void => {
    if (mode === 'email') {
      setLoginModalConfig({
        ...loginModalConfig,
        value,
        email: value
      })
    } else if (mode === 'code') {
      setLoginModalConfig({
        ...loginModalConfig,
        value,
      })
    }
  }

  const closeLoginModalWindow = (action?: string): void => {
    if (action === 'back') {
      setLoginModalConfig({
        ...loginModalConfig,
        mode: 'email',
        value: '',
        methodId: '',
        email: '',
        loadingOTP: false
      })
    } else {
      setLoginModalConfig({
        ...loginModalConfig,
        open: false,
        loadingOTP: false
      })
    }
  }

  const attemptAdminMode = async (mode: string): Promise<void> => {
    try {
      if (mode === 'email') {
        const res = await diamondStore.startOTP(loginModalConfig.email)
        setLoginModalConfig({
          ...loginModalConfig,
          loadingOTP: true
        })

        if (res.success) {
          setLoginModalConfig({
            ...loginModalConfig,
            methodId: res.payload.method_id,
            mode: 'code',
            value: '',
            loadingOTP: false
          })
        } else {
          closeLoginModalWindow()
          setError({
            title: t('common.error'),
            message: res.info.error_message
          })
          setErrorModalOpen(true)
        }
      } else if (mode === 'code') {
        setLoginModalConfig({
          ...loginModalConfig,
          loadingOTP: true
        })
        const res = await diamondStore.validateOTP(loginModalConfig.methodId, loginModalConfig.value)
        if (res.success) {
          setIsMemoMode(true)
          const role = res.payload.user.is_admin ? Roles.Merchant : Roles.Customer
          setRole(role)
          localStorage.setItem(USER_COOKIE_NAME, JSON.stringify(res.payload))
          setUserData(res.payload)
          closeLoginModalWindow()
          const url = new URL(window.location.href);
          url.searchParams.delete("admin");
          window.history.replaceState({}, "", url.toString());
          wishlistProps?.refresh(role, true)
        } else {
          closeLoginModalWindow()
          setError({
            title: t('common.error'),
            message: serverErrorMessage(res?.payload) || res?.info?.error_message
          })
          setErrorModalOpen(true)
        }
      }
    } catch (e) {
      logger.info(e)
    }
  }

  const getFavorites = (): any[] => {
    const favorites = localStorage.getItem('favorites_gemstones')
    return favorites ? JSON.parse(favorites) : []
  }

  const bookmarksEmail = async (): Promise<void> => {
    const favorites = getFavorites()
    if (!favorites.length) {
      setError({
        title: t('bookmarksModal.noStarredGemstones'),
        message: t('bookmarksModal.starOneGemstone')
      })
      setErrorModalOpen(true)
      return
    }
    const skus = favorites.map(item => item.sku)
    const link = window.location.origin + '/apps/ub/curated_gemstones/' + btoa(skus.join(','))

    const text = link
    const input = document.createElement('input')
    input.setAttribute('value', text)
    document.body.appendChild(input)
    input.select()
    document.execCommand('copy')
    document.body.removeChild(input)
    setBookmarksModalOpen(true)
  }

  const handleDASOrdering = (diamondsData: { [key: string]: any }, orderType: string): void => {
    const gems = Array.isArray(diamondsData) ? diamondsData : [diamondsData]
    setSelectedGems(gems)
    setSelectedGemsOrderType(orderType)
    setConfirmationModalOpen(true)
  }

  const handleAddToDAS = useCallback(async (): Promise<void> => {
    const result = await storeFunctions.addToDAS({
      diamonds: selectedGems,
      type: selectedGemsOrderType,
      shop: shopName || '',
      searchType: 'gemstone_search'
    })
    if (!result.success) {
      setConfirmationModalOpen(false)
      setError({
        title: result.error?.errorTitle || '',
        message: result.error?.errorBody || ''
      })
      setErrorModalOpen(true)
      return
    }
    setConfirmationModalOpen(false)
    setDasOrderSuccessfullModalOpen(true)
  }, [selectedGems, selectedGemsOrderType, shopName])

  const handleAddToCart = async (gems: { [key: string]: any }[]): Promise<void> => {
    const result = await storeFunctions.addToCart(gems, {}, 'gemstone_search', 'gemstone')
    if (!result.success) {
      setError({
        title: result.error?.errorTitle || '',
        message: result.error?.errorBody || ''
      })
      setErrorModalOpen(true)
      return
    }
    window.location.href = '/cart'
  }

  const handleCopy = (gemstone: Diamond) => {
    if (navigator && navigator.clipboard) {
      navigator.clipboard.writeText(`${window.location.origin}/apps/ub/gemstone/${gemstone.sku}`)
    }
  }

  const searchParams = new URLSearchParams(window.location.search)
  const jType = searchParams.get('jewelry_type')
  const mode =
    jType == 'ring'
      ? JewelryType.Ring
      : jType == 'pendant'
        ? JewelryType.Pendant
        : jType == 'bracelet'
          ? JewelryType.Bracelet
          : jType == 'earring'
            ? JewelryType.Earring
            : ''

  const gemstoneSearchProps: GemstoneSearchProps = {
    role,
    onEmailClick: bookmarksEmail,
    stickyHeaderTop: 0,
    merchant: { company_name: shopName || '', first_name: '' },
    isOnboarded: true,
    isActivated: true,
    api: {
      searchProducts: getDiamonds,
      countProducts: getDiamondsCount
    },
    onParamsChange: onParamsChange,
    getDefaultParams: () => getDefaultParams(props.location.search),
    ...(config.advancedSearchCartEnabled ? { onAddCartItem: handleAddToCart } : {}),
    ...(isMemoMode
      ? {
          onAddMemoItem: (item: { [key: string]: any }): void => handleDASOrdering(item, 'm'),
          onAddInStoreItem: (item: { [key: string]: any }): void => handleDASOrdering(item, 'i')
        }
      : {}),
    ...(process.env.VIEWER_URL ? { viewerUrl: process.env.VIEWER_URL } : {}),
    onSelectedDiamondChange: ({ sku }: { sku: string }): void =>
      onSelectedDiamondChange({ sku }),
    currencyProps: {
      mode: role === Roles.Merchant || isMemoMode ? 'double' : 'single',
      currencies: currencyInfo.currencies,
      currencyCode: currencyInfo.currency,
      // Second (customer-currency) line = retail_price x this rate. Use the Shopify
      // presentment rate so it matches checkout/other surfaces (not DAS's rate). In
      // single mode retail_price is already converted, so rate is 1 (no re-multiply).
      currencyRate:
        role === Roles.Merchant || isMemoMode
          ? shopCurrency && currencyInfo.currency !== shopCurrency
            ? window.Shopify?.currency?.rate || 1
            : 1
          : 1
    },
    disableFiltersWhenSKU: true,
    enabledDiamondGemTypes: enabledDiamondGemTypes,
    onDetailsClick: (item: { [key: string]: any }, isCustomerLink: boolean): void => {
      let pathPart = 'merchant/gemstone'
      if (
        (isCustomerLink && role === Roles.Merchant) ||
        (!isCustomerLink && role !== Roles.Merchant)
      ) {
        pathPart = 'gemstone'
      }
      const jewelryType = jType !== null ? `?jewelry_type=${jType}` : ''
      window.open(
        `${window.location.origin}/apps/ub/${pathPart}/${item.sku}${jewelryType}`,
        '_blank'
      )
    },
    v360PlayerUrl: `${process.env.VIEWER_URL}/api/360player`,
    nativeV360PlayerUrl: `${location.origin}/apps/ub/360player`,
    labels: typeLabels,
    ringSizeProps: {
      sizeRegion: config.ringSizeRegion
    }
  }

  if (role === Roles.Merchant || isMemoMode) {
    gemstoneSearchProps.wishlistProps = wishlistProps
  }

  return (
    <div className="unbridaled-search-page">
      <LoginModal
        loginModalConfig={{
          ...loginModalConfig,
          ...getLoginModalLabels(t, loginModalConfig.mode, {
            emailTitle: t('loginModal.confirmEmail'),
            codeConfirm: t('common.submit')
          })
        }}
        onConfirm={attemptAdminMode}
        onClose={closeLoginModalWindow}
        onChange={handleLoginModalValueChange}
      />
      <ErrorModal
        open={errorModalOpen}
        title={error?.title}
        message={error?.message}
        onClose={closeErrorModal}
      />
      <BookmarksModal
        open={bookmarksModalOpen}
        shopName={shopName}
        favorites={wishlistProps?.products || []}
        config={config}
        onClose={closeBookmarksModal}
        role={role}
        isMemoMode={isMemoMode}
        title={t('common.emailGemstones', 'Email Gemstones')}
        stoneType="gemstone"
      />
      <ConfirmationModal
        open={confirmationModalOpen}
        gems={selectedGems}
        stoneType="gemstone"
        orderType={selectedGemsOrderType}
        config={config}
        onConfirm={handleAddToDAS}
        onClose={closeConfirmationModal}
      />
      <DASOrderSuccessfullModal
        open={dasOrderSuccessfullModalOpen}
        gems={selectedGems}
        orderType={selectedGemsOrderType}
        config={config}
        onClose={closeDASOrderSuccessfullModal}
      />
      {loaded ? (
        <>
          <GemstoneSearchPage
            key={`gemstones-search-${role}-${isMemoMode ? 'memo' : 'default'}`}
            locale={getSearchSdkLocale()}
            shareProps={{ hidden: true }}
            overrides={{
              ...(role === Roles.Customer && isMemoMode ? { filtersMode: 'merchant' } : {}),
              dataView: {
                headerActions:
                  role === Roles.Merchant ? (
                    <Menu className="flex" placement="bottom-start">
                      <MenuButton
                        as={Button}
                        className={classNames(
                          'uui-flex uui-items-center uui-h-fit uui-h-[30px]',
                          isMobile && 'uui-px-0'
                        )}
                        disabled={false}
                        size={'small'}
                        text={!isMobile ? 'Exit' : ''}
                        RightIcon={
                          !isMobile && (
                            <ArrowHeadDownIcon
                              size="14px"
                              color="uui-text-white"
                              className="uui-hidden sm:uui-block"
                            />
                          )
                        }
                      >
                        {isMobile && (
                          <LogoutIcon className="uui-mr-1.5 uui-ml-1.5" color="uui-text-white" />
                        )}
                      </MenuButton>
                      <MenuItems>
                        <MenuLabel
                          title={t('common.memoMode', 'Memo Mode')}
                          variant="default"
                          onClick={onSwitchToMemoMode}
                          disabled={false}
                        />
                        <MenuLabel
                          title={t('common.logout', 'Logout')}
                          variant="destructive"
                          onClick={onLogout}
                          disabled={false}
                        />
                      </MenuItems>
                    </Menu>
                  ) : isMemoMode ? (
                    <Button
                      text={!isMobile ? 'Exit' : ''}
                      className={classNames(
                        'uui-flex uui-items-center uui-h-fit uui-h-[30px]',
                        isMobile && 'uui-px-0'
                      )}
                      onClick={onLogout}
                      disabled={false}
                    >
                      {isMobile && (
                        <LogoutIcon className="uui-mr-1.5 uui-ml-1.5" color="uui-text-white" />
                      )}
                    </Button>
                  ) : undefined
              }
            }}
            {...gemstoneSearchProps}
          />
        </>
      ) : (
        <Preloader preloaderClass="unbridaled-full-page-preloader" />
      )}
    </div>
  )
}

export default GemstonesMainSearchPage
